import React, { Suspense, useState, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
// side effects
import "lazysizes";
/**
 * lazysizes attribute change / re-initialization extension;
 * @see https://github.com/aFarkas/lazysizes/tree/gh-pages/plugins/attrchange#lazysizes-attribute-change--re-initialization-extension
 */
import "lazysizes/plugins/attrchange/ls.attrchange";
import { Helmet } from "react-helmet";
// hooks
import { useUser } from "contexts/UserContext";
import { useAuthModals } from "contexts/auth-modals-context";
// styles
import "./App.scss";
// utils
import { reportError, reportProblem } from "utils/frontend_error_reporting";
import shouldSuppressError from "utils/shouldSuppressError";
import * as amplitude from "@amplitude/analytics-browser";
// constants
import { GENERAL_ROUTES } from "constants/generalRoutes";
import { analytics } from "constants/segment";
// components
import AppNavigation from "components/app-navigation/AppNavigation";
import Layout from "components/layout";
import Loading from "components/Loading";
import Intercom from "intercom";
import { IS_DEV_ENV } from "constants/isDevEnv";
import { AMPLITUDE_PUBLIC_KEY } from "constants/amplitudePublicKey";
import WriteForUs from "components/write-for-us/WriteForUs";
import MeasurementBasedCare from "components/measurement-based-care/MeasurementBasedCare";
import PartnerWithOrchid from "components/partner-with-orchid/PartnerWithOrchid";
import SavingsCalculator from "components/savings-calculator/SavingsCalculator";
import SwitchToOrhid from "components/switch-to-orchid/SwitchToOrchid";
import OrchidAI from "components/orchid-ai/OrchidAI";
import FAQPage from "components/faq-page/FAQPage";
import EHRSecurity from "components/ehr-security/EHRSecurity";
import getCanonicalLink from "utils/getCanonicalLink";
import {
  ALL_POSTS_BLOG_CATEGORY,
  BLOG_CATEGORIES,
} from "components/Blogging/constants/blog-categories";
import {
  ALL_PRESS_CATEGORY,
  PRESS_CATEGORIES,
} from "components/press/constants/press-categories";
import FullWidthImage from "components/full-width-image";
import lazy from "utils/lazyOrReload";
import GetStartedDropdown from "components/auth/GetStartedDropdown";
import QuickIntake from "components/quick-intake/QuickIntake";
import AppointmentCheckout from "components/AppointmentBook/AppointmentCheckout";

const PostBlog = lazy(
  () => import("./components/Blogging/components/post-blog/PostBlog"),
);
const MemberHome = lazy(() => import("components/Homepage/member-home"));

const OrchidProHomeWeb = lazy(
  () => import("./components/Homepage/orchid-pro-home-web"),
);
const VideoChat = lazy(() => import("./components/video-chat/VideoChat"));
const VideoChatV2 = lazy(
  () => import("./components/video-chat-v2/VideoChatV2"),
);
const AboutUs = lazy(() => import("./components/about-us"));

const AllGroupSessionList = lazy(
  () => import("./components/GroupSession/AllGroupSessionList"),
);
const GroupSessionSeriesPage = lazy(
  () => import("./components/GroupSession/GroupSessionSeriesPage"),
);
const GroupSeriesCheckout = lazy(
  () => import("./components/GroupSession/GroupSeriesCheckout"),
);
const GroupSessionCheckoutConfirm = lazy(
  () => import("./components/GroupSession/checkout-confirm/CheckoutConfirm"),
);

const Welcome = lazy(() => import("./components/auth/Welcome"));
const WelcomeToOrchid = lazy(() => import("components/auth/WelcomeToOrchid"));

const OrchidProRegister = lazy(
  () => import("./components/auth/orchid-pro-register/OrchidProRegister"),
);
const MemberRegister = lazy(
  () => import("./components/auth/member-register/MemberRegister"),
);
const UserLoginForm = lazy(
  () => import("./components/auth/user-login-form/UserLoginForm"),
);
const ResetPasswordPage = lazy(
  () => import("./components/auth/ResetPasswordPage"),
);

const MarketPlace = lazy(() => import("./components/MarketPlace/MarketPlace"));
const InfoPage = lazy(
  () => import("./components/MarketPlace/info-page/InfoPage"),
);
const PracticeGroup = lazy(
  () => import("components/MarketPlace/practice-group"),
);
const PaymentConfirmation = lazy(
  () => import("./components/AppointmentBook/payment-confirm/PaymentConfirm"),
);

const BlogList = lazy(
  () => import("./components/Blogging/components/blog-list/BlogList"),
);
const Blog = lazy(() => import("./components/Blogging/Blog"));
const PostBlogConfirmation = lazy(
  () =>
    import(
      "./components/Blogging/components/post-blog-confirmation/PostBlogConfirmation"
    ),
);
const ProfileUrlInputPage = lazy(
  () => import("./components/provider-intake/profile-url"),
);

const ThanksForSharing = lazy(
  () => import("./components/DataScience/thanks-for-sharing"),
);

const AUP = lazy(() => import("./components/Terms/AUP"));
const TermsOfUse = lazy(() => import("./components/Terms/TermsOfUse"));
const WaiverOfLiability = lazy(
  () => import("./components/Terms/WaiverOfLiability"),
);
const PrivacyPolicy = lazy(() => import("./components/Terms/Privacy"));
const AIPrivacyPolicy = lazy(
  () => import("./components/Terms/AIPrivacyPolicy"),
);
const IndependentContractorAgreement = lazy(
  () => import("./components/Terms/IndependentContractor"),
);

const DoctorDashboard = lazy(
  () => import("./components/Dashboard/DoctorDashboard/DoctorDashboard"),
);
const MemberDashboard = lazy(
  () => import("./components/Dashboard/MemberDashboard/MemberDashboard"),
);

const ZoomIntegrationDocumentation = lazy(
  () =>
    import(
      "./components/zoom-integration-documentation/ZoomIntegrationDocumentation"
    ),
);

const Support = lazy(() => import("./components/support/Support"));

const HowDoYouFeel = lazy(
  () => import("./components/DataScience/how-do-you-feel"),
);
const YourDailyMoods = lazy(
  () => import("./components/DataScience/your-daily-moods"),
);

const GoogleAuthResult = lazy(
  () => import("components/Dashboard/DoctorDashboard/views/google-auth-result"),
);

const PageNotFound = lazy(() => import("./components/page-not-found"));
const UpdatePreference = lazy(
  () => import("./components/email-preference/update-preference"),
);
const UnsubscribeFromAll = lazy(
  () => import("./components/email-preference/unsubscribe-from-all"),
);

const EmailPreference = lazy(() => import("./components/email-preference"));
const Resubscribe = lazy(
  () => import("./components/email-preference/resubscribe"),
);
const PricingPage = lazy(
  () => import("./components/Subscription/pricing-page/Pricing"),
);

const SubscriptionCheckout = lazy(
  () =>
    import(
      "./components/Subscription/subscription-checkout/SubscriptionCheckout"
    ),
);

const CheckoutAbandoned = lazy(
  () =>
    import(
      "./components/Subscription/subscription-checkout/checkout-abandoned/CheckoutAbandoned"
    ),
);

const ClientPortal = lazy(
  () => import("./components/client-portal/ClientPortal"),
);
const Pressroom = lazy(() => import("./components/press/Pressroom"));

const Press = lazy(() => import("./components/press/Press"));

window.onerror = function (msg, url, lineNo, columnNo, error) {
  if (IS_DEV_ENV) {
    return;
  }
  shouldSuppressError(msg);
  if (error) {
    reportError({
      reporter_module: "global_error_handler",
      context: { msg, url, lineNo, columnNo },
      err: error,
    });
  } else {
    reportProblem({
      reporter_module: "global_error_handler",
      context: { msg, url, lineNo, columnNo },
      frontend_error: msg.toString(),
    });
  }
  return false;
};

const LOGIN_BACKGROUND = "/img/video/login-background.png";

export default function App() {
  const { pathname } = useLocation();
  const { user } = useUser();
  const authModalsProps = useAuthModals();
  /**
   * disable navbar scrolling
   */
  const [disableNavbarScrolling, setDisableNavbarScrolling] = useState(false);

  const pageHeightCallback = (dataFromChild) => {
    if (dataFromChild < 1000) {
      setDisableNavbarScrolling(true);
    }
  };

  useEffect(() => {
    if (pathname.endsWith("/member-intake-form")) {
      authModalsProps.setCustomMemberIntakeFormWrapperProps({
        isMemberIntakeFormIncomplete: false,
      });
      authModalsProps.openMemberIntakeFormWrapper();
    }
  }, [pathname, authModalsProps]);

  useEffect(() => {
    if (
      user === null &&
      (pathname.startsWith("/pro-dashboard") ||
        pathname.startsWith("/member-dashboard"))
    ) {
      return authModalsProps.openAuthModals();
    }
  }, [user, pathname, authModalsProps]);

  analytics.page(useLocation());

  if (!IS_DEV_ENV) {
    amplitude.init(AMPLITUDE_PUBLIC_KEY);
  }

  return (
    <>
      <Helmet>
        <title>Orchid | Online Professional Therapy & Support Groups</title>
        <meta
          name="description"
          content="This is private practice — made perfect. Meet Orchid, the most efficient EHR for mental health professionals &amp; practitioners that takes the guesswork out of growing."
        />

        <link rel="canonical" href={getCanonicalLink()} />

        <meta property="og:site_name" content="Orchid" />
        <meta property="og:locale" content="en_US" />
        <meta
          property="og:title"
          content="Orchid | Online Professional Therapy & Support Groups"
        />
        <meta
          property="og:description"
          content="This is private practice — made perfect. Meet Orchid, the most efficient EHR for mental health professionals &amp; practitioners that takes the guesswork out of growing."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={getCanonicalLink()} />
        <meta
          property="og:image"
          content="https://orchid.exchange/orchid-logo-meta-tag.png"
        />
        <meta
          property="og:image:secure_url"
          content="https://orchid.exchange/orchid-logo-meta-tag.png"
        />
        <meta property="og:image:alt" content="Orchid Logo" />
        <meta property="og:image:width" content="2501" />
        <meta property="og:image:height" content="1397" />
        <meta property="og:image:type" content="image/png" />

        <meta
          name="twitter:title"
          content="Orchid | The Most Efficient EHR for Mental Health"
        />
        <meta
          name="twitter:description"
          content="This is private practice — made perfect. Meet Orchid, the most efficient EHR for mental health professionals &amp; practitioners that takes the guesswork out of growing."
        />
        <meta
          name="twitter:image"
          content="https://orchid.exchange/orchid-logo-meta-tag.png"
        />
        <meta
          name="twitter:image:src"
          content="https://orchid.exchange/orchid-logo-meta-tag.png"
        />
        <meta name="twitter:image:alt" content="Orchid Logo" />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      {window.location.hostname.endsWith(".secureportal.health") ||
      window.location.hostname === "127.0.0.1" ? (
        <Layout>
          <Suspense fallback={<Loading />}>
            <ClientPortal />
          </Suspense>
        </Layout>
      ) : pathname.startsWith("/pro-dashboard/session-review") &&
        !user?.signup_form_finished ? (
        <Layout>
          <FullWidthImage
            src={LOGIN_BACKGROUND}
            alt={"Post session summary page background"}
          />
        </Layout>
      ) : (
        <Layout>
          <Intercom />
          <AppNavigation disableNavbarScrolling={disableNavbarScrolling} />
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/pro-dashboard/*" element={<DoctorDashboard />} />
              <Route path="/member-dashboard/*" element={<MemberDashboard />} />
              <Route path="/" element={<MemberHome />} />
              <Route
                path="/professional-homepage"
                element={<OrchidProHomeWeb />}
              />
              <Route
                path="/pricing/checkout-cancel"
                element={<CheckoutAbandoned />}
              />
              <Route path="/pricing" element={<PricingPage />} />
              <Route
                path="/pricing/:sub_class/:sub_period/:type"
                element={<SubscriptionCheckout />}
              />
              <Route path="/get-started" element={<GetStartedDropdown />} />
              <Route path="/login" element={<UserLoginForm />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/member-intake-form" element={<div />} />
              <Route
                path="/professional-register"
                element={<OrchidProRegister />}
              />
              <Route path="/member-register" element={<MemberRegister />} />
              <Route
                path="/group-session"
                element={<AllGroupSessionList user={user} />}
              />
              <Route
                path="/group-session/series/:group_sessions_series_id"
                element={<GroupSessionSeriesPage user={user} />}
              />
              <Route
                path="/group-session/series/:group_sessions_series_id/checkout"
                element={<GroupSeriesCheckout />}
              />
              <Route
                path="/group-session/series/:group_sessions_series_id/checkout-confirm"
                element={<GroupSessionCheckoutConfirm />}
              />
              <Route path="/AUP" element={<AUP />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route
                path="/clinician-ai-privacy-policy"
                element={<AIPrivacyPolicy />}
              />
              <Route
                path="/waiver-of-liability"
                element={<WaiverOfLiability />}
              />
              <Route
                path="/independent-contractor-agreement"
                element={<IndependentContractorAgreement />}
              />
              <Route path="/confirm-email" element={<WelcomeToOrchid />} />
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/quick-intake" element={<QuickIntake />} />
              <Route path="/profile-input" element={<ProfileUrlInputPage />} />

              <Route
                path="/payment-confirmation"
                element={<PaymentConfirmation />}
              />
              <Route path="/video-chat" element={<VideoChat />} />
              <Route path="/video-chat-2" element={<VideoChatV2 />} />
              <Route
                path={GENERAL_ROUTES.press}
                element={<Pressroom initialCategory={ALL_PRESS_CATEGORY} />}
              />
              {PRESS_CATEGORIES.filter((category) => category.value !== "").map(
                (category) => (
                  <Route
                    key={category.value}
                    path={`/press/${category.value}`}
                    element={<Pressroom initialCategory={category} />}
                  />
                ),
              )}
              <Route path="/press/:press_url" element={<Press />} />
              <Route
                path={GENERAL_ROUTES.blogs}
                element={<BlogList initialCategory={ALL_POSTS_BLOG_CATEGORY} />}
              />
              {BLOG_CATEGORIES.filter((category) => category.value !== "").map(
                (category) => (
                  <Route
                    key={category.value}
                    path={`/blogs/${category.value}`}
                    element={<BlogList initialCategory={category} />}
                  />
                ),
              )}
              <Route path="/blogs/:url" element={<Blog />} />
              <Route path="/post-blog" element={<PostBlog />} />
              <Route
                path="/post-blog-confirmation"
                element={<PostBlogConfirmation />}
              />
              <Route path="/professional-search" element={<MarketPlace />} />
              <Route
                path="/zoom-integration-documentation"
                element={<ZoomIntegrationDocumentation />}
              />
              <Route path="/support" element={<Support />} />
              <Route path="/book" element={<MarketPlace />} />
              <Route
                path={`/professionals/:initialStateUrl`}
                element={<MarketPlace />}
              />
              <Route
                path={`/psychologists/:initialStateUrl?`}
                element={<MarketPlace initialSpecialty="Psychologist" />}
              />
              <Route
                path={`/psychiatrists/:initialStateUrl?`}
                element={<MarketPlace initialSpecialty="Psychiatry" />}
              />
              <Route
                path={`/therapists/:initialStateUrl?`}
                element={<MarketPlace initialSpecialty="Therapist" />}
              />
              <Route
                path="/thanks-for-sharing"
                element={<ThanksForSharing />}
              />
              <Route
                path="/professional-search/:id_or_custom_url"
                element={<InfoPage pageHeightCallback={pageHeightCallback} />}
              />
              <Route
                path="/professional-search/:id_or_custom_url/checkout"
                element={<AppointmentCheckout />}
              />
              <Route
                path="/professional-search/:id_or_custom_url/checkout-confirm/:timeslot_id"
                element={<PaymentConfirmation />}
              />
              <Route path="/about-us" element={<AboutUs />} />
              <Route
                path={"/measurement-based-care"}
                element={<MeasurementBasedCare />}
              />
              <Route
                path={"/partner-with-orchid"}
                element={<PartnerWithOrchid />}
              />
              <Route path={"/write-for-us"} element={<WriteForUs />} />
              <Route path={"/switch-ehr-orchid"} element={<SwitchToOrhid />} />
              <Route
                path={"/ehr-cost-savings"}
                element={<SavingsCalculator />}
              />
              <Route
                path={"/ai-progress-note-generator"}
                element={<OrchidAI />}
              />
              <Route path={"/orchid-ehr-faq"} element={<FAQPage />} />
              <Route path={"/ehr-security"} element={<EHRSecurity />} />
              <Route path="/feelings" element={<HowDoYouFeel />} />
              <Route path="/daily-moods" element={<YourDailyMoods />} />
              {/* Google Calendar feature */}
              {/* google auth redirect */}
              <Route
                path={GENERAL_ROUTES.googleCalendarSuccess}
                element={<GoogleAuthResult />}
              />
              <Route
                path={GENERAL_ROUTES.googleCalendarFailure}
                element={<GoogleAuthResult />}
              />
              <Route path="/page-not-found" element={<PageNotFound />} />
              <Route
                path="/email-preference/update-preference"
                element={<UpdatePreference />}
              />
              <Route
                path="/email-preference/unsubscribe"
                element={<UnsubscribeFromAll />}
              />
              <Route path="/email-preference" element={<EmailPreference />} />
              <Route
                path="/email-preference/resubscribe"
                element={<Resubscribe />}
              />
              <Route path="/:practice_group_name" element={<PracticeGroup />} />
              <Route path="*" element={<PageNotFound />} />
            </Routes>
          </Suspense>
        </Layout>
      )}
    </>
  );
}
